import OccConfirm from '@app/components/modal/OccConfirm';
import userStore from '@app/stores/userStore';
import { hotelLabel } from '@app/utils/activeHotel';
import {
  clearPendingPush,
  pushSwitchTarget,
  savePendingPush,
  takePendingPush,
} from '@app/utils/pushHotel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * CSV-02 MH-5b: 푸시 알림을 눌렀을 때 그 알림이 다른 접근 호텔 것이면 전환을 확인합니다.
 * PC(AuthLayout)·모바일(MobileAuthLayout)의 알림 처리 함수 맨 앞에서 interceptPush를 부르고,
 * true면 기존 이동을 멈춥니다. 확인하면 알림을 보관하고 범위를 전환하며(화면을 새로 엶),
 * 새로 열린 화면에서 작업 호텔이 정해지면 보관한 알림을 onReplay로 한 번 다시 엽니다.
 *
 * @param {(message: object) => void} onReplay 알림 처리 함수(전환 뒤 다시 열 때 사용)
 */
export default function usePushHotelSwitch(onReplay) {
  const { t } = useTranslation();
  const activeHotelId = userStore((state) => state.myInfo?.hotel?.hotelId);
  const [pending, setPending] = useState(null);
  const [switching, setSwitching] = useState(false);
  const replayRef = useRef(onReplay);
  replayRef.current = onReplay;

  // 전환 뒤 새로 열린 화면: 작업 호텔이 정해지면 보관한 알림을 한 번 엽니다.
  useEffect(() => {
    if (activeHotelId == null) return;
    const message = takePendingPush(activeHotelId);
    if (message) replayRef.current(message);
  }, [activeHotelId]);

  // 알림 처리 함수가 마운트 때 한 번 등록되므로, 최신 상태를 store에서 직접 읽습니다.
  const interceptPush = useCallback((message) => {
    const { myInfo, myHotels } = userStore.getState();
    const hotel = pushSwitchTarget(message, myInfo, myHotels);
    if (!hotel) return false;
    setPending({ message, hotel });
    return true;
  }, []);

  const confirm = async () => {
    setSwitching(true);
    savePendingPush(pending.message);
    try {
      await userStore.getState().switchHotel(pending.hotel.hotelId);
    } catch (e) {
      // 실패 안내는 공통 axios 처리기가 띄웁니다. 오류 객체에는 토큰이 있어 상태 코드만 남깁니다.
      console.log(
        'push hotel switch failed :',
        e?.response?.status ?? e?.message,
      );
      clearPendingPush();
      setSwitching(false);
      setPending(null);
    }
  };

  const confirmElement = pending ? (
    <OccConfirm
      title={t('common:scope.pushTitle')}
      confirmName="common:scope.switch"
      confirmBtnDisabled={switching}
      onCancel={() => setPending(null)}
      onConfirm={confirm}
      content={
        <p style={{ margin: 0 }}>
          {t('common:scope.pushBody', { hotel: hotelLabel(pending.hotel) })}
        </p>
      }
    />
  ) : null;

  return { interceptPush, confirmElement };
}
